import React from "react";
import { Edit2, Trash2, Search } from "lucide-react";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";

export interface SunbedRoom {
  id: string;
  label: string;
  status: "Available" | "In-session" | "Maintenance" | "Cleaning" | "Cooling";
  totalDuration: string;
}

interface SunbedsTableProps {
  rooms: SunbedRoom[];
  indexOfFirstItem: number;
  onEdit: (room: SunbedRoom) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

export const SunbedsTable: React.FC<SunbedsTableProps> = ({
  rooms,
  indexOfFirstItem,
  onEdit,
  onDelete,
  isLoading = false,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Available":
        return "bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]";
      case "In-session":
        return "bg-[#E3F2FD] text-[#1565C0] border-[#90CAF9]";
      case "Maintenance":
        return "bg-[#FFEBEE] text-[#C62828] border-[#EF9A9A]";
      case "Cleaning":
        return "bg-[#FFF8E1] text-[#F57F17] border-[#FFE082]";
      case "Cooling":
        return "bg-[#E0F7FA] text-[#00695C] border-[#80DEEA]";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  return (
    <div className="flex-1 overflow-y-auto relative">
      <table className="w-full">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100">
          <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
            <th className="col-span-1 text-left font-bold">#</th>
            <th className="col-span-3 text-left font-bold">Label</th>
            <th className="col-span-3 text-left font-bold">Availability</th>
            <th className="col-span-4 text-left font-bold">Total Duration</th>
            <th className="col-span-1 text-right font-bold">Actions</th>
          </tr>
        </thead>
        <tbody className="w-full divide-y divide-gray-100 bg-white">
          {isLoading ? (
            <tr className="h-64 w-full">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : rooms.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full">
              <td className="w-full h-full flex flex-col items-center justify-center">
                <Search className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-lg font-medium text-gray-500">
                  No rooms found
                </p>
                <p className="text-sm">Try adjusting your search terms</p>
              </td>
            </tr>
          ) : (
            <>
              {rooms.map((room, idx) => (
                <tr
                  key={room.id}
                  className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-purple-50/30 transition-colors bg-white group text-sm w-full"
                >
                  <td className="col-span-1 text-gray-500 font-medium">
                    {indexOfFirstItem + idx + 1}
                  </td>
                  <td className="col-span-3 font-semibold text-gray-800 group-hover:text-[#885ABB] transition-colors">
                    {room.label}
                  </td>
                  <td className="col-span-3">
                    <span
                      className={cn(
                        "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                        getStatusColor(room.status)
                      )}
                    >
                      {room.status}
                    </span>
                  </td>
                  <td className="col-span-4 text-gray-600 font-medium">
                    {room.totalDuration}
                  </td>
                  <td className="col-span-1 flex justify-end gap-2 transition-opacity">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onEdit(room)}
                      className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Edit2 size={15} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onDelete(room.id)}
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={15} />
                    </Button>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
